import { v4 as uuidv4 } from "uuid";

import ReactDOM from "react-dom";
import { makeAutoObservable, reaction } from "mobx";
import { observer } from "mobx-react-lite";
import Food from "./Food";
import { FoodItemProperties, MealNames } from "../../Resources/Constants";
import Meal from "./Meal";
import FoodOption from "./FoodOption";

class NutritionPlan {
  meals = [];

  showingEditGoals = false;
  showingByPercentages = false;

  originalPlanId = null;

  auxCaloriesGoal = 0;
  auxCarbsGoal = 0;
  auxProteinGoal = 0;
  auxFatsGoal = 0;

  auxCarbsGoalPercentage = 0;
  auxProteinGoalPercentage = 0;
  auxFatsGoalPercentage = 0;

  weekDay = null;

  constructor(
    id = uuidv4(),
    name = "",
    notes = "",
    caloriesGoal = 0,
    carbsGoal = 0,
    proteinGoal = 0,
    fatsGoal = 0,
    client = null,
    coach = null
  ) {
    this.id = id;
    this.name = name;
    this.notes = notes;
    this.caloriesGoal = caloriesGoal;
    this.carbsGoal = carbsGoal;
    this.proteinGoal = proteinGoal;
    this.fatsGoal = fatsGoal;
    this.client = client;
    this.coach = coach;
    makeAutoObservable(this);
  }

  uploadToCloud() {
    let mealsArray = this.meals.map((meal) => meal.uploadToCloud());

    return {
      id: this.id.toString().toLowerCase(),
      name: this.name,
      notes: this.notes,
      // order: this.order,
      calories_goal: this.caloriesGoal,
      carbs_goal: this.carbsGoal,
      protein_goal: this.proteinGoal,
      fats_goal: this.fatsGoal,
      meals: mealsArray,
    };
  }

  fetchMeal(mealData) {
    const newMeal = new Meal(
      mealData.id,
      mealData.name,
      mealData.notes,
      mealData.order,
      mealData.default_food_option,
      this
    );

    mealData?.food_options.forEach((food_option) => {
      newMeal.fetchFoodOption(food_option);
    });

    this.meals.push(newMeal);
  }

  addMeal() {
    const maxOrder = this.meals.length
      ? Math.max(...this.meals.map((m) => m.order))
      : -1;
    const newOrder = maxOrder + 1;

    const newMeal = new Meal(
      uuidv4(),
      MealNames.BREAKFAST,
      undefined,
      newOrder,
      undefined,
      this
    );

    const firstFoodOption = new FoodOption(uuidv4(), undefined, newMeal);

    firstFoodOption.setProperty("isSelected", true);

    newMeal.foodOptions.push(firstFoodOption);

    this.meals.push(newMeal);
  }

  deleteMeal(mealId) {
    const idx = this.meals.findIndex((m) => m.id === mealId);

    if (idx > -1) {
      // already selected → remove it
      this.meals.splice(idx, 1);
    }
  }

  get canSaveAux() {
    // you probably want to make sure there's at least one meal
    if (this.meals.length === 0) return false;

    // return true only if every meal's default option has at least one food
    return (
      this.name !== "" &&
      this.meals.every(
        (meal) =>
          Array.isArray(meal.getDefaultFoodOption?.foods) &&
          meal.getDefaultFoodOption.foods.length > 0
      )
    );
  }

  saveAuxPlanChanges() {
    const clientOrCoach = this.client ?? this.coach;

    if (!clientOrCoach) return;

    if (this.originalPlanId) {
      const originalPlan = clientOrCoach.nutritionPlans.find(
        (np) => np.id === this.originalPlanId
      );

      if (!originalPlan) return;

      originalPlan.setProperty("name", this.name);
      originalPlan.setProperty("notes", this.notes);

      originalPlan.setProperty("caloriesGoal", this.caloriesGoal);
      originalPlan.setProperty("carbsGoal", this.carbsGoal);
      originalPlan.setProperty("proteinGoal", this.proteinGoal);
      originalPlan.setProperty("fatsGoal", this.fatsGoal);

      // originalPlan.setProperty("meals", []);
      if (typeof originalPlan.meals.replace === "function") {
        originalPlan.meals.replace([]);
      } else {
        originalPlan.meals.splice(0);
      }
      this.meals.forEach((meal) => {
        meal.setProperty("nutritionPlan", originalPlan);
      });
      originalPlan.meals.push(...this.meals);

      if (this.coach) {
        this.coach.updateCoachTransactionNutritionPlans(originalPlan.id);
        this.coach.startOrResetBatchTimer();
      }

      if (this.client) {
        this.client.updateClientTransactionNutritionPlans(originalPlan.id);
        this.client.startOrResetBatchTimer();
      }
    } else {
      clientOrCoach.nutritionPlans.push(this);

      if (this.coach) {
        this.coach.updateCoachTransactionNutritionPlans(this.id);
        this.coach.startOrResetBatchTimer();
      }

      if (this.client) {
        this.client.updateClientTransactionNutritionPlans(this.id);
        this.client.startOrResetBatchTimer();
      }
    }
  }

  get selectedMeal() {
    return this.meals.find((meal) => meal.isSelected);
  }

  setSelectedMeal(selectedMeal) {
    this.meals.forEach((meals) => {
      if (meals.id === selectedMeal.id) {
        meals.setProperty("isSelected", true);
      } else {
        meals.setProperty("isSelected", false);
      }
    });
  }

  getMacroTotal(macro) {
    let total = 0;

    this.meals.forEach((meal) => {
      // if (!meal.getDefaultFoodOption) return;

      if (macro === "calories") {
        total += meal.getDefaultFoodOption.totalCalories;
      } else if (macro === "carbs") {
        total += meal.getDefaultFoodOption.totalCarbs;
      } else if (macro === "protein") {
        total += meal.getDefaultFoodOption.totalProtein;
      } else if (macro === "fats") {
        total += meal.getDefaultFoodOption.totalFats;
      }
    });

    return total;
  }

  resetAuxGoals() {
    this.auxCaloriesGoal = this.caloriesGoal;
    this.auxCarbsGoal = this.carbsGoal;
    this.auxProteinGoal = this.proteinGoal;
    this.auxFatsGoal = this.fatsGoal;

    if (this.auxCaloriesGoal > 0) {
      this.auxCarbsGoalPercentage =
        ((this.auxCarbsGoal * 4) / this.auxCaloriesGoal) * 100;
      this.auxProteinGoalPercentage =
        ((this.auxProteinGoal * 4) / this.auxCaloriesGoal) * 100;
      this.auxFatsGoalPercentage =
        ((this.auxFatsGoal * 9) / this.auxCaloriesGoal) * 100;
    } else {
      this.auxCarbsGoalPercentage = 0;
      this.auxProteinGoalPercentage = 0;
      this.auxFatsGoalPercentage = 0;
    }
  } //Done

  savePlanGoals() {
    console.log("savePlanGoals");
    if (this.showingByPercentages) {
      console.log("savePlanGoals if");
      const newCaloriesGoal = this.auxCaloriesGoal;

      this.carbsGoal =
        ((newCaloriesGoal / 4) * this.auxCarbsGoalPercentage) / 100;
      this.proteinGoal =
        ((newCaloriesGoal / 4) * this.auxProteinGoalPercentage) / 100;
      this.fatsGoal =
        ((newCaloriesGoal / 9) * this.auxFatsGoalPercentage) / 100;

      this.caloriesGoal = newCaloriesGoal;

      console.log("savePlanGoals : ", this.carbsGoal);
    } else {
      const carbsValue = this.auxCarbsGoal * 4;
      const proteinValue = this.auxProteinGoal * 4;
      const fatsValue = this.auxFatsGoal * 9;

      this.carbsGoal = this.auxCarbsGoal;
      this.proteinGoal = this.auxProteinGoal;
      this.fatsGoal = this.auxFatsGoal;

      this.caloriesGoal = carbsValue + proteinValue + fatsValue;
    }
  }

  setGoalProperty(propertyName, value) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      if (propertyName === "auxCaloriesGoal") {
        if (value > 0) {
          // const carbsPercent =
          //   this.auxCaloriesGoal > 0
          //     ? this.auxCarbsGoal / this.auxCaloriesGoal
          //     : 0;
          // const proteinPercent =
          //   this.auxCaloriesGoal > 0
          //     ? this.auxProteinGoal / this.auxCaloriesGoal
          //     : 0;
          // const fatsPercent =
          //   this.auxCaloriesGoal > 0
          //     ? this.auxFatsGoal / this.auxCaloriesGoal
          //     : 0;

          // this.auxCarbsGoal = value * carbsPercent;
          // this.auxProteinGoal = value * proteinPercent;
          // this.auxFatsGoal = value * fatsPercent;

          this.auxCarbsGoal = ((value / 4) * this.auxCarbsGoalPercentage) / 100;
          this.auxProteinGoal =
            ((value / 4) * this.auxProteinGoalPercentage) / 100;
          this.auxFatsGoal = ((value / 9) * this.auxFatsGoalPercentage) / 100;
        } else {
          this.auxCarbsGoal = 0;
          this.auxProteinGoal = 0;
          this.auxFatsGoal = 0;
        }

        this[propertyName] = value;
      } else {
        this[propertyName] = value;

        const carbsValue = this.auxCarbsGoal * 4;
        const proteinValue = this.auxProteinGoal * 4;
        const fatsValue = this.auxFatsGoal * 9;

        const sum = carbsValue + proteinValue + fatsValue;

        this.auxCaloriesGoal = sum;

        if (sum > 0) {
          // if (propertyName === "auxCarbsGoal") {
          //   this.auxCarbsGoalPercentage = (carbsValue / sum) * 100;
          // } else if (propertyName === "auxProteinGoal") {
          //   this.auxProteinGoalPercentage = (proteinValue / sum) * 100;
          // } else {
          //   this.auxFatsGoalPercentage = (fatsValue / sum) * 100;
          // }
          this.auxCarbsGoalPercentage = (carbsValue / sum) * 100;
          this.auxProteinGoalPercentage = (proteinValue / sum) * 100;
          this.auxFatsGoalPercentage = (fatsValue / sum) * 100;
        } else {
          if (propertyName === "auxCarbsGoal") {
            this.auxCarbsGoalPercentage = 0;
          } else if (propertyName === "auxProteinGoal") {
            this.auxProteinGoalPercentage = 0;
          } else {
            this.auxFatsGoalPercentage = 0;
          }
        }
      }
    } else {
      console.error(`Property ${propertyName} does not exist.`);
    }
  } //Done

  setGoalPercentage(macro, value) {
    const percent = value > 0 ? value / 100 : 0;
    const total = this.auxCaloriesGoal;
    if (macro === "carbs") {
      this.auxCarbsGoal = (total * percent) / 4;
      this.auxCarbsGoalPercentage = value;
    } else if (macro === "protein") {
      this.auxProteinGoal = (total * percent) / 4;
      this.auxProteinGoalPercentage = value;
    } else {
      this.auxFatsGoal = (total * percent) / 9;
      this.auxFatsGoalPercentage = value;
    }
  } //Done

  getMacroAuxGoalPercentage(macro) {
    const value =
      macro === "carbs"
        ? this.auxCarbsGoalPercentage
        : macro === "protein"
        ? this.auxProteinGoalPercentage
        : this.auxFatsGoalPercentage;

    return value > 0 ? value : 0;
  } //Done

  // getMacroAuxGoalGramsByPercentage(macro) {
  //   const value =
  //     macro === "carbs"
  //       ? this.auxCarbsGoal * 4
  //       : macro === "protein"
  //       ? this.auxProteinGoal * 4
  //       : this.auxFatsGoal * 9;

  //   return this.getGoalCaloriesByFormula > 0
  //     ? (value / this.getGoalCaloriesByFormula) * 100
  //     : 0;
  // }

  get getGoalCaloriesByFormula() {
    const carbsValue = this.auxCarbsGoal * 4;
    const proteinValue = this.auxProteinGoal * 4;
    const fatsValue = this.auxFatsGoal * 9;

    return carbsValue + proteinValue + fatsValue;
  }

  get percentagesSumError() {
    if (this.auxCaloriesGoal > 0) {
      // const carbCals = this.auxCarbsGoal * 4;
      // const proteinCals = this.auxProteinGoal * 4;
      // const fatCals = this.auxFatsGoal * 9;

      const carbPercentage = this.auxCarbsGoalPercentage;
      const proteinPercentage = this.auxProteinGoalPercentage;
      const fatPercentage = this.auxFatsGoalPercentage;

      const sum = carbPercentage + proteinPercentage + fatPercentage;

      const allGreater =
        carbPercentage > 0 && proteinPercentage > 0 && fatPercentage > 0;

      return allGreater && sum !== 100 && this.showingByPercentages;
    }
    return false;
  }

  get canSaveGoals() {
    const allPercentages =
      this.auxCarbsGoalPercentage > 0 &&
      this.auxProteinGoalPercentage > 0 &&
      this.auxFatsGoalPercentage > 0;

    const sumTo100 =
      this.auxCarbsGoalPercentage +
        this.auxProteinGoalPercentage +
        this.auxFatsGoalPercentage ===
      100;

    return (
      !this.showingByPercentages ||
      (this.auxCaloriesGoal > 0 && allPercentages && sumTo100)
    );
  }

  copyNutritionPlanToLibrary() {
    if (this.client.coach) {
      const newNutritionPlan = new NutritionPlan(
        uuidv4(),
        this.name,
        this.notes,
        this.caloriesGoal,
        this.carbsGoal,
        this.proteinGoal,
        this.fatsGoal,
        null,
        this.client.coach
      );

      this.meals.forEach((meal) => {
        const newMeal = new Meal(
          uuidv4(),
          meal.name,
          meal.notes,
          meal.order,
          meal.defaultFoodOption,
          newNutritionPlan
        );

        meal.foodOptions.forEach((foodOption) => {
          const newFoodOption = new FoodOption(
            uuidv4(),
            foodOption.order,
            newMeal
          );

          foodOption.foods.forEach((food) => {
            const newFoodToAdd = new Food(
              uuidv4(),
              food.name,
              food.order,
              food.quantity,
              food.calories,
              food.carbs,
              food.protein,
              food.fats,
              food.code,
              food.brand,
              food.store,
              newFoodOption
            );

            food.equivalences.forEach((equivalence) => {
              const newEquivalenceToAdd = new Food(
                uuidv4(),
                equivalence.name,
                equivalence.order,
                equivalence.quantity,
                equivalence.calories,
                equivalence.carbs,
                equivalence.protein,
                equivalence.fats,
                equivalence.code,
                equivalence.brand,
                equivalence.store,
                null,
                null,
                null,
                newFoodToAdd
              );

              newFoodToAdd.equivalences.push(newEquivalenceToAdd);
            });

            newFoodOption.foods.push(newFoodToAdd);
          });

          newMeal.foodOptions.push(newFoodOption);
        });

        newNutritionPlan.meals.push(newMeal);
      });

      this.client.coach.nutritionPlans.push(newNutritionPlan);
      this.client.coach.updateCoachTransactionNutritionPlans(
        newNutritionPlan.id
      );
      this.client.coach.startOrResetBatchTimer();
    }
  }

  // Generic setter
  setProperty(propertyName, value) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      this[propertyName] = value;
    } else {
      console.error(`Property ${propertyName} does not exist.`);
    }
  }

  // Generic getter
  getProperty(propertyName) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      return this[propertyName];
    } else {
      console.error(`Property ${propertyName} does not exist.`);
      return undefined;
    }
  }
}
export default NutritionPlan;
